import { type Routes } from '@angular/router';

import { UserRole } from '@protctc/common/core/models/user/user-role';
import { AuthorizedGuard } from '@protctc/common/core/guards/authorized.guard';
import { UnauthorizedGuard } from '@protctc/common/core/guards/unauthorized.guard';
import { merchantReadyGuard } from '@protctc/common/core/guards/merchant-ready.guard';

import { MainLayoutComponent } from './modules/shared/layouts/main-layout/main-layout.component';

/** Invoice token param. */
export const INVOICE_TOKEN_PARAM = 'invoiceToken';

/** App routes. */
export const appRoutes: Routes = [
	{
		path: '',
		component: MainLayoutComponent,
		canActivate: [AuthorizedGuard],
		data: { roles: [UserRole.BusinessOwner] },
		children: [
			{
				path: 'dashboard',
				data: { breadcrumb: 'Dashboard' },
				loadChildren: () => import('./modules/dashboard/dashboard.routes').then(m => m.dashboardRoutes),
			},
			{
				path: 'invoice-settings',
				loadChildren: () => import('./modules/invoice-settings/invoice-settings.routes').then(m => m.invoiceSettingsRoutes),
			},
			{
				path: 'invoices',
				data: { breadcrumb: 'Invoices' },
				canMatch: [merchantReadyGuard],
				loadChildren: () => import('./modules/invoices/invoices.routes').then(m => m.invoicesRoutes),
			},
			{
				path: 'estimates',
				data: { breadcrumb: 'Estimates' },
				loadChildren: () => import('./modules/estimates/estimates.routes').then(m => m.estimatesRoutes),
			},
			{
				path: 'line-items',
				data: { breadcrumb: 'Products & Services' },
				loadChildren: () => import('./modules/line-items/line-items.routes').then(m => m.lineItemsRoutes),
			},
			{
				path: 'users',
				data: { breadcrumb: 'User management' },
				loadChildren: () => import('./modules/users/users.routes').then(m => m.usersRoutes),
			},
			{
				path: 'customers',
				data: { breadcrumb: 'Customers' },
				loadChildren: () => import('./modules/customers/customers.routes').then(m => m.customersRoutes),
			},
			{
				path: 'billing-payment',
				data: { breadcrumb: 'Billing & Payments' },
				canMatch: [merchantReadyGuard],
				loadChildren: () => import('./modules/billing-payment/billing-payment.routes').then(m => m.billingPaymentRoutes),
			},
			{
				path: 'schedule',
				data: { breadcrumb: 'Schedule' },
				loadChildren: () => import('./modules/schedule/schedule.routes').then(m => m.scheduleRoutes),
			},
			{
				path: 'account-settings',
				loadChildren: () => import('./modules/account-settings/account-settings.routes').then(m => m.accountSettingsRoutes),
			},
			{ path: '', redirectTo: 'dashboard', pathMatch: 'full' },
		],
	},
	{
		path: 'admin',
		canActivate: [AuthorizedGuard],
		loadChildren: () => import('./modules/admin/admin.routes').then(m => m.adminRoutes),
		data: { roles: [UserRole.SuperAdmin] },
	},
	{
		path: 'auth',
		canActivate: [UnauthorizedGuard],
		loadChildren: () => import('./modules/auth/auth.routes').then(m => m.authRoutes),
	},
	{
		path: 'payment',
		loadChildren: () => import('./modules/customer-invoice-payment/customer-invoice-payment.routes')
			.then(m => m.customerInvoicePaymentRoutes),
	},
	{
		path: `invoice-preview/:${INVOICE_TOKEN_PARAM}`,
		loadComponent: () => import('./modules/invoice-preview/invoice-preview.component')
			.then(c => c.InvoicePreviewComponent),
	},
	{
		path: `estimate/:estimateToken`,
		loadComponent: () => import('./modules/estimate-public-view/estimate-public-view.component')
			.then(c => c.EstimatePublicViewComponent),
	},
	{
		path: 'general-info',
		loadChildren: () => import('./modules/general-info/general-info.routes')
			.then(m => m.generalInfoRoutes),
	},
	{ path: '**', redirectTo: 'dashboard' },
];
